import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { renderAsync } from 'docx-preview';
import { DocumentViewerStoreService } from '../../../core/service/document-viewer-store/document-viewer-store.service';
import { TitleService } from '../../../core/service/title-service/title.service';

@Component({
  selector: 'app-documents-view-page',
  standalone: false,
  templateUrl: './documents-view-page.component.html',
  styleUrls: ['./documents-view-page.component.scss'],
})
export class DocumentsViewPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private docViewerStore = inject(DocumentViewerStoreService);
  private titleService = inject(TitleService);
  private destroy$ = new Subject<void>();

  entity = signal<any | null>(null);
  documents = signal<any[]>([]);
  isLoading = signal(true);
  error = signal<string | null>(null);
  activeDocumentId = signal<string>('');
  activeGroupType = signal<string>('');
  activeFileId = signal<string>('');

  entityId = signal<string>('');
  documentId = signal<string>('');
  entityType = signal<'joueur' | 'manager' | 'staff' | 'club'>('joueur');
  returnUrl = signal<string>('');

  // Documents grouped by document_type for the two-level navbar
  documentGroups = computed(() => {
    const docs = this.documents();
    const map = new Map<string, any[]>();
    docs.forEach(doc => {
      const key = doc.document_type || String(doc.file_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(doc);
    });
    return Array.from(map.entries()).map(([type, files]) => ({
      type,
      name_fr: files[0].document_name_fr || files[0].document_type || files[0].file_name,
      name_ar: files[0].document_name_ar || null,
      files,
    }));
  });

  activeGroup = computed(() =>
    this.documentGroups().find(g => g.type === this.activeGroupType()) || this.documentGroups()[0] || null
  );

  activeFile = computed(() => {
    const group = this.activeGroup();
    if (!group) return null;
    return group.files.find((f: any) => String(f.file_id) === this.activeFileId()) || group.files[0] || null;
  });

  activeFileUrl = computed(() => {
    const f = this.activeFile();
    return f ? this.fileUrl(f) : '';
  });

  activeFileExt = computed(() => {
    const f = this.activeFile();
    if (!f?.file_name) return '';
    const parts = f.file_name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  });

  // The detail endpoints do not all name the signed link the same way (demande: serve_url, licence / manager detail: signed_url).
  // Never fall back to the raw file_link when a signed one exists: it is a relative storage path and renders as a broken image.
  private fileUrl(f: any): string {
    return f.serve_url || f.signed_url || f.url || f.file_link || '';
  }

  safePdfUrl = computed((): SafeResourceUrl => {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.activeFileUrl());
  });

  // Word rendering state: idle | loading | ready | error
  wordState = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  wordError = signal<string | null>(null);

  entityTypeDisplay = computed(() => {
    const type = this.entityType();
    const typeMap: { [key: string]: string } = {
      joueur: 'Joueur',
      manager: 'Manager',
      staff: 'Entraîneur',
      club: 'Club',
    };
    return typeMap[type] || type;
  });

  // Title: who the documents belong to, and whether they are the documents of a DEMANDE or of the licence record.
  //   demande  → "Documents pour la demande de KARIM CHAMEKH"
  //   licence  → "Documents pour KARIM CHAMEKH"
  pageTitle = computed(() => {
    const e = this.entity();
    if (!e) return 'Documents';
    const person = (first?: string, last?: string) => (first && last ? `${first} ${last}` : '');
    const name =
      person(e.player_first_name, e.player_last_name) ||
      person(e.manager_first_name, e.manager_last_name) ||
      person(e.staff_first_name, e.staff_last_name) ||
      person(e.referee_first_name, e.referee_last_name) ||
      person(e.data_first_name, e.data_last_name) ||
      person(e.person_first_name, e.person_last_name) ||
      person(e.first_name, e.last_name) ||
      e.data_club_name || e.club_name || '';
    const isDemande = !!(e.demande_documents || e.demande_type || e.demande_status || e.demande_id_public);
    if (!name) return 'Documents';
    return isDemande ? `Documents pour la demande de ${name}` : `Documents pour ${name}`;
  });

  ngOnInit(): void {
    this.titleService.setPageTitle('Documents');
    this.cleanUrl();

    const cleanInterval = setInterval(() => this.cleanUrl(), 10);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => {
        if (window.location.href.includes('?')) {
          window.history.replaceState({}, '', window.location.href.split('?')[0]);
        }
      });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(() => this.cleanUrl(), 0);
    });

    this.destroy$.subscribe(() => clearInterval(cleanInterval));

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params: any) => {
      const entityId = params['joueurId'] || params['managerId'] || params['staffId'] || params['id'] || '';
      this.entityId.set(entityId);
      this.documentId.set(params['documentId'] || '');

      const type = this.route.snapshot.data['entityType'] || 'joueur';
      this.entityType.set(type);

      this.loadEntityAndDocuments();
    });
  }

  private cleanUrl(): void {
    if (window.location.href.includes('?')) {
      window.history.replaceState({}, '', window.location.href.split('?')[0]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadEntityAndDocuments(): void {
    this.isLoading.set(true);
    this.error.set(null);

    const navigation = this.router.getCurrentNavigation();
    const state = (navigation?.extras?.state as any) || (history.state as any);
    let entityData = state?.entityData;
    if (state?.returnUrl) this.returnUrl.set(state.returnUrl);

    if (!entityData) {
      entityData = this.docViewerStore.retrieve();
    }

    if (entityData) {
      this.entity.set(entityData);
      this.fetchDocuments();
    } else {
      this.isLoading.set(false);
      this.error.set("Impossible de charger les données de l'entité. Veuillez revenir en arrière et réessayer.");
    }
  }

  private fetchDocuments(): void {
    const entity = this.entity();

    if (entity && entity.documents && Array.isArray(entity.documents)) {
      const types: any[] = entity.documentTypes || [];
      const enriched = entity.documents.map((doc: any) => {
        if (types.length && doc.document_type) {
          const type = types.find((t: any) => t.document_code === doc.document_type);
          if (type) {
            return {
              ...doc,
              document_name_fr: doc.document_name_fr || type.document_name_fr || null,
              document_name_ar: doc.document_name_ar || type.document_name_ar || null,
            };
          }
        }
        return doc;
      });
      this.documents.set(enriched);

      const docId = this.documentId();
      let initialFile = docId ? enriched.find((d: any) => String(d.file_id) === docId) : null;
      if (!initialFile && enriched.length > 0) initialFile = enriched[0];

      if (initialFile) {
        const groupKey = initialFile.document_type || String(initialFile.file_id);
        this.activeGroupType.set(groupKey);
        this.activeFileId.set(String(initialFile.file_id));
        this.activeDocumentId.set(String(initialFile.file_id));
        this.maybeLoadWord(initialFile);
      }

      this.isLoading.set(false);
    } else {
      this.isLoading.set(false);
      this.error.set('Aucun document disponible pour cette entité.');
    }
  }

  goBack(): void {
    const returnUrl = this.returnUrl();
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    const params = this.route.snapshot.params;
    const type = this.entityType();
    const isFederation = this.router.url.includes('/federation/');
    let backUrl = '';

    const hasValidIds = (...ids: any[]) => ids.every(v => v && v !== '0');

    if (type === 'joueur') {
      const joueurId = params['joueurId'];
      const relationId = params['relationId'];
      if (hasValidIds(joueurId, relationId)) {
        backUrl = isFederation
          ? `/federation/joueurs/${joueurId}/${relationId}`
          : `/Joueurs/${joueurId}/${relationId}`;
      }
    } else if (type === 'manager') {
      const managerId = params['managerId'];
      const relationId = params['relationId'];
      if (hasValidIds(managerId, relationId)) {
        backUrl = isFederation
          ? `/federation/managers/${managerId}/${relationId}`
          : `/Dirigeants/${managerId}/${relationId}`;
      }
    } else if (type === 'staff') {
      const staffId = params['staffId'];
      const relationId = params['relationId'];
      if (hasValidIds(staffId, relationId)) {
        backUrl = isFederation
          ? `/federation/staff/${staffId}/${relationId}`
          : `/Staff/${staffId}/${relationId}`;
      }
    } else if (type === 'club') {
      const clubId = params['clubId'];
      if (hasValidIds(clubId)) {
        backUrl = isFederation ? `/federation/clubs/${clubId}` : `/club`;
      }
    }

    if (backUrl) {
      this.router.navigateByUrl(backUrl);
    } else {
      window.history.back();
    }
  }

  closeDocuments(): void { this.goBack(); }

  selectDocument(documentId: string): void { this.activeDocumentId.set(documentId); }

  selectGroup(groupType: string): void {
    this.activeGroupType.set(groupType);
    const group = this.documentGroups().find(g => g.type === groupType);
    if (group && group.files.length > 0) {
      const firstFileId = String(group.files[0].file_id);
      this.activeFileId.set(firstFileId);
      this.activeDocumentId.set(firstFileId);
      this.maybeLoadWord(group.files[0]);
    }
  }

  selectFile(fileId: string): void {
    this.activeFileId.set(fileId);
    this.activeDocumentId.set(fileId);
    const file = this.activeFile();
    if (file) this.maybeLoadWord(file);
  }

  private maybeLoadWord(file: any): void {
    const ext = (file.file_name || '').split('.').pop()?.toLowerCase() || '';
    if (ext !== 'docx') { this.wordState.set('idle'); return; }
    const url = this.fileUrl(file);
    if (!url) return;
    this.loadWordDocument(url);
  }

  private loadWordDocument(url: string): void {
    this.wordState.set('loading');
    this.wordError.set(null);

    // Clear old preview container content
    const oldContainer = document.getElementById('docx-preview-container');
    if (oldContainer) oldContainer.innerHTML = '';

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then(buffer => {
        // Give Angular time to render the container div after wordState = 'loading'
        return new Promise<void>((resolve, reject) => {
          setTimeout(async () => {
            try {
              const container = document.getElementById('docx-preview-container');
              if (!container) throw new Error('Preview container not found in DOM');

              await renderAsync(buffer as ArrayBuffer, container, undefined, {
                className: 'docx-preview',
                inWrapper: true,
                ignoreWidth: false,
                ignoreHeight: false,
                renderHeaders: true,
                renderFooters: true,
                renderFootnotes: true,
                renderEndnotes: true,
              });

              resolve();
            } catch (e) {
              reject(e);
            }
          }, 50);
        });
      })
      .then(() => this.wordState.set('ready'))
      .catch(err => {
        console.error('[DocViewer] Word load error:', err);
        this.wordError.set('Impossible de charger le document Word.');
        this.wordState.set('error');
      });
  }

  downloadActiveFile(): void {
    const file = this.activeFile();
    if (!file) return;
    const url = this.fileUrl(file);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  onDocumentChange(_doc: any): void {}
}
